// src/pages/FastKenoGamePage.jsx - Production Ready (No hardcoded variants)
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import PageWrapper from "../components/PageWrapper";
import KenoBall from "../components/KenoBall";
import BraunClock from "../components/BraunClock";
import axiosInstance from "../axiosInstance";
import { LanguageContext } from "../contexts/LanguageContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FastKenoGamePage = (props) => {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);

  // State declarations
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [drawnNumbers, setDrawnNumbers] = useState([]);
  const [currentDrawBall, setCurrentDrawBall] = useState(null);
  const [sessionStatus, setSessionStatus] = useState("waiting");
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [myTickets, setMyTickets] = useState([]);
  const [stakeAmount, setStakeAmount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("game");
  const [gameHistory, setGameHistory] = useState([]);
  const [statistics, setStatistics] = useState({
    totalGames: 0,
    totalWins: 0,
    totalWinAmount: 0,
  });
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawProgress, setDrawProgress] = useState(0);
  const [currentGameId, setCurrentGameId] = useState(null);
  const [topWinners, setTopWinners] = useState([]);
  const [recentDraws, setRecentDraws] = useState([]);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [showWinToast, setShowWinToast] = useState(false);
  const [lastWinAmount, setLastWinAmount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [rotateCurrentBall, setRotateCurrentBall] = useState(false);
  const [gameEndMessage, setGameEndMessage] = useState(null);
  const [hasFetchedResults, setHasFetchedResults] = useState(false);
  const [showPrizeTable, setShowPrizeTable] = useState(true);
  const [maxTickets, setMaxTickets] = useState(2);
  const [expandedDraws, setExpandedDraws] = useState({});
  const [variantsLoaded, setVariantsLoaded] = useState(false);
  const [apiError, setApiError] = useState(false);

  // Variant-related state
  const [selectedVariant, setSelectedVariant] = useState(10);
  const [availableVariants, setAvailableVariants] = useState({});
  const [prizeMultipliers, setPrizeMultipliers] = useState({});
  const [numbersRequired, setNumbersRequired] = useState(10);
  const [variantAllowedStakes, setVariantAllowedStakes] = useState([]);
  const [variantMinStake, setVariantMinStake] = useState(10);
  const [variantMaxStake, setVariantMaxStake] = useState(1000);
  const [userSelectedVariant, setUserSelectedVariant] = useState(null);

  // Refs
  const timerIntervalRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastBallRef = useRef(null);
  const lastGameIdRef = useRef(null);
  const toastTimeoutRef = useRef(null);

  const TOTAL_NUMBERS = 80;

  const toggleDrawExpand = (drawId) => {
    setExpandedDraws((prev) => ({
      ...prev,
      [drawId]: !prev[drawId],
    }));
  };

  // Translations
  const t = (key) => {
    const translations = {
      en: {
        title: "KENO",
        gameId: "Game ID",
        timeRemaining: "Time Remaining",
        players: "Players",
        myTickets: "My Tickets",
        selectNumbers: "Select Numbers",
        quick: "Quick",
        clear: "Clear",
        addTicket: "Add Ticket",
        drawInProgress: "Draw in Progress",
        progress: "Progress",
        drawnNumbers: "Drawn Numbers",
        gameCompleted: "Game Completed",
        youWon: "You Won",
        betterLuck: "Better luck next time!",
        newGameStarting: "Next game starting soon...",
        active: "Active",
        won: "Won",
        lost: "Lost",
        bet: "Bet",
        numbers: "nums",
        matches: "Matches",
        history: "History",
        results: "Results",
        stats: "Stats",
        leaderboard: "Leaderboard",
        totalGames: "Games",
        winRate: "Win Rate",
        wins: "Wins",
        totalWinAmount: "Total Won",
        winners: "Winners",
        noTickets: "No tickets yet",
        selectHint: "Select your numbers and add a ticket",
        loading: "Loading...",
        pleaseWait: "Please wait for the game to start",
        drawInProgressMsg: "Draw in progress, cannot select numbers",
        gameCompletedMsg: "Game completed, next game starting soon",
        maxNumbers: "Select exactly",
        selectExactly: "Select exactly",
        numbersToPick: "numbers",
        cannotAddTicket: "Cannot add ticket at this time",
        ticketAdded: "Ticket added!",
        failedAddTicket: "Failed to add ticket",
        youWonToast: "You Won!",
        currentBall: "Current Ball",
        prizeTable: "Prize Table",
        hidePrizes: "Hide",
        showPrizes: "Show",
        multiplier: "x",
        picks: "picks",
        possibleWin: "Win",
        ticketsRemaining: "Tickets left",
        maxTicketsReached: "Maximum tickets reached",
        yourTickets: "Your Tickets",
        id: "ID",
        showMore: "Show all",
        showLess: "Show less",
        yourStats: "Your Stats",
        topWinners: "Top Winners",
        noWinnersYet: "No winners yet",
        rank: "Rank",
        player: "Player",
        amount: "Amount",
        recentDraws: "Recent Draws",
        noDrawsYet: "No draws yet",
        drawNumber: "Draw",
        selectVariant: "Game Type",
        ticketsAllowed: "Max Tickets",
        minBet: "Min",
        maxBet: "Max",
        errorLoading: "Error loading game data. Please refresh the page.",
        retry: "Retry",
      },
      am: {
        title: "ኬኖ",
        gameId: "የጨዋታ መታወቂያ",
        timeRemaining: "የቀረ ጊዜ",
        players: "ተጫዋቾች",
        myTickets: "የኔ ቲኬቶች",
        selectNumbers: "ቁጥሮች ምረጡ",
        quick: "ፈጣን",
        clear: "አጽዳ",
        addTicket: "ቲኬት ጨምሩ",
        drawInProgress: "እጣ በሂደት ላይ",
        progress: "ሂደት",
        drawnNumbers: "የወጡ ቁጥሮች",
        gameCompleted: "ጨዋታ ተጠናቋል",
        youWon: "አሸንፈዋል",
        betterLuck: "በሚቀጥለው ጊዜ መልካም እድል!",
        newGameStarting: "ቀጣይ ጨዋታ ይጀምራል...",
        active: "ንቁ",
        won: "አሸንፏል",
        lost: "ተሸንፏል",
        bet: "ውርርድ",
        numbers: "ቁጥሮች",
        matches: "ተዛማጅ",
        history: "ታሪክ",
        results: "ውጤቶች",
        stats: "ስታቲስቲክስ",
        leaderboard: "ከፍተኛ አሸናፊዎች",
        totalGames: "ጨዋታዎች",
        winRate: "የማሸነፍ መጠን",
        wins: "ድሎች",
        totalWinAmount: "ጠቅላላ ሽልማት",
        winners: "አሸናፊዎች",
        noTickets: "ምንም ቲኬት የለም",
        selectHint: "ቁጥሮች ይምረጡ እና ቲኬት ጨምሩ",
        loading: "በመጫን ላይ...",
        pleaseWait: "እባክዎ ጨዋታው እስኪጀመር ይጠብቁ",
        drawInProgressMsg: "እጣ በሂደት ላይ ነው, ቁጥሮች መምረጥ አይቻልም",
        gameCompletedMsg: "ጨዋታው ተጠናቋል, ቀጣዩ ጨዋታ ይጀምራል",
        maxNumbers: "በትክክል መምረጥ አለብዎት",
        selectExactly: "በትክክል ይምረጡ",
        numbersToPick: "ቁጥሮች",
        cannotAddTicket: "በዚህ ጊዜ ቲኬት መጨመር አይቻልም",
        ticketAdded: "ቲኬት ተጨምሯል!",
        failedAddTicket: "ቲኬት መጨመር አልተሳካም",
        youWonToast: "አሸንፈዋል!",
        currentBall: "አሁን የወጣው ኳስ",
        prizeTable: "የሽልማት ሰንጠረዥ",
        hidePrizes: "ደብቅ",
        showPrizes: "አሳይ",
        multiplier: "x",
        picks: "ምርጫ",
        possibleWin: "ሽልማት",
        ticketsRemaining: "የቀሩ ቲኬቶች",
        maxTicketsReached: "ከፍተኛ ቲኬቶች ደርሰዋል",
        yourTickets: "የእርስዎ ቲኬቶች",
        id: "መታወቂያ",
        showMore: "ሁሉንም አሳይ",
        showLess: "አጭር አሳይ",
        yourStats: "የእርስዎ ስታቲስቲክስ",
        topWinners: "ከፍተኛ አሸናፊዎች",
        noWinnersYet: "እስካሁን አሸናፊ የለም",
        rank: "ደረጃ",
        player: "ተጫዋች",
        amount: "መጠን",
        recentDraws: "የቅርብ ጊዜ እጣዎች",
        noDrawsYet: "እስካሁን እጣ አልተካሄደም",
        drawNumber: "እጣ",
        selectVariant: "የጨዋታ አይነት",
        ticketsAllowed: "ከፍተኛ ቲኬቶች",
        minBet: "ዝቅተኛ",
        maxBet: "ከፍተኛ",
        errorLoading: "የጨዋታ ውሂብ በመጫን ላይ ችግር ተፈጥሯል. እባክዎ ገፁን ያድሱት።",
        retry: "ድገም",
      },
      or: {
        title: "KEENOO",
        gameId: "ID Peericha",
        timeRemaining: "Yeroo Hafee",
        players: "Taphattoota",
        myTickets: "Tikkeetii Koo",
        selectNumbers: "Lakkoofsa Filadhu",
        quick: "Sassii",
        clear: "Qulqulleessi",
        addTicket: "Tikkeetii Dabali",
        drawInProgress: "Argannoo Irratti",
        progress: "Itooffii",
        drawnNumbers: "Lakkoofsa Ba'an",
        gameCompleted: "Peerichiin Xumurame",
        youWon: "Mo'atte",
        betterLuck: "Yeroo ittaanu carraa gaarii!",
        newGameStarting: "Peerichiin ittaanu eegala...",
        active: "Hojiirraa",
        won: "Mo'ate",
        lost: "Dhuwufte",
        bet: "Saantima",
        numbers: "lakkoofsa",
        matches: "Walqixxummaa",
        history: "Seenaa",
        results: "Bu'aa",
        stats: "Gabatee",
        leaderboard: "Mo'attoota Olii",
        totalGames: "Peerichota",
        winRate: "Moo'ina",
        wins: "Mo'annoowwan",
        totalWinAmount: "Mo'annaa Waliigalaa",
        winners: "Mo'attoota",
        noTickets: "Tikkeettiin hin jiru",
        selectHint: "Lakkoofsa filadhuu fi tikkeetii dabali",
        loading: "Fe'amaa jira...",
        pleaseWait: "Mee peerichiin eegaluusaa eegaa",
        drawInProgressMsg: "Argannoo irratti, lakkoofsi filamuu hin danda'u",
        gameCompletedMsg: "Peerichaan xumurame, peerichiin ittaanu eegala",
        maxNumbers: "Sirriitti filachuu qabdu",
        selectExactly: "Sirriitti filadhu",
        numbersToPick: "lakkoofsa",
        cannotAddTicket: "Yeroo kana tikkeetii dabaluu hin danda'u",
        ticketAdded: "Tikkeetiin dabalamte!",
        failedAddTicket: "Tikkeetiin dabalamuu hin dandeenye",
        youWonToast: "Mo'atte!",
        currentBall: "Kubbayyaa Ammaa",
        prizeTable: "Gabatee Mo'annoowwan",
        hidePrizes: "Dhoksaa",
        showPrizes: "Addaansaa",
        multiplier: "x",
        picks: "Filannoo",
        possibleWin: "Mo'anna",
        ticketsRemaining: "Tikkeetii hafte",
        maxTicketsReached: "Tikkeetii ol'aantti ga'e",
        yourTickets: "Tikkeettiin Keessan",
        id: "ID",
        showMore: "Hunda agarsiisi",
        showLess: "Xiqqeessa agarsiisi",
        yourStats: "Gabatee Keessan",
        topWinners: "Mo'attoota Olii",
        noWinnersYet: "Mo'attoonni hin jiran",
        rank: "Sadarkaa",
        player: "Taphattoo",
        amount: "Qaba",
        recentDraws: "Argannoo Dhumaa",
        noDrawsYet: "Argannoon hin jiru",
        drawNumber: "Argannoo",
        selectVariant: "Gosa Peerichaa",
        ticketsAllowed: "Tikkeetii Ol'aantii",
        minBet: "Xiqqaa",
        maxBet: "Ol'aantaa",
        errorLoading: "Dogoggora fe'uu gadii. Mee fuula haaromsi.",
        retry: "Itti yaali",
      },
      ti: {
        title: "ኬኖ",
        gameId: "መለለዪ ጸወታ",
        timeRemaining: "ዝተረፈ ግዜ",
        players: "ተጻወትቲ",
        myTickets: "ቲከታተይ",
        selectNumbers: "ቁጽሪታት ምረጽ",
        quick: "ቅልጡፍ",
        clear: "ኣጽረይ",
        addTicket: "ቲከት ምሓል",
        drawInProgress: "ዕጽብ ኣብ ምሃልሃል",
        progress: "ምሃልሃል",
        drawnNumbers: "ዝወጹ ቁጽሪታት",
        gameCompleted: "ጸወታ ተዛዚሙ",
        youWon: "ተሰናኢልካ",
        betterLuck: "ኣብ ዚቕጽል ግዜ ጽቡቕ ዕድል!",
        newGameStarting: "ዚቕጽል ጸወታ ክጅምር እዩ...",
        active: "ንጡፍ",
        won: "ተሰናኢሉ",
        lost: "ተሓሊመ",
        bet: "ተካፈለ",
        numbers: "ቁጽሪታት",
        matches: "ዝተመዓረበ",
        history: "ታሪኽ",
        results: "ውጽኢታት",
        stats: "ስታቲስቲክሳት",
        leaderboard: "ላዕለዎት ተሓተንቲ",
        totalGames: "ጸወታታት",
        winRate: "ምዕወታ",
        wins: "ዕወታታት",
        totalWinAmount: "ሽልማት",
        winners: "ተሓተንቲ",
        noTickets: "ቲከት የለን",
        selectHint: "ቁጽሪታት ምረጽ ከምኡውን ቲከት ምሓል",
        loading: "ይጽዓን ኣሎ...",
        pleaseWait: "በጃኻ ጸወታ ክጅምር ምትኣሳሰር",
        drawInProgressMsg: "ዕጽብ ኣብ ምሃልሃል እዩ, ቁጽሪታት ክትምርጽ ኣይከኣልን",
        gameCompletedMsg: "ጸወታ ተዛዚሙ, ዚቕጽል ጸወታ ክጅምር እዩ",
        maxNumbers: "ብትክክል ክትምርጽ ኣለካ",
        selectExactly: "ብትክክል ምረጽ",
        numbersToPick: "ቁጽሪታት",
        cannotAddTicket: "ኣብዚ ግዜ ቲከት ምሓል ኣይከኣልን",
        ticketAdded: "ቲከት ተወሲኹ!",
        failedAddTicket: "ቲከት ምሓል ኣይተኻኣለን",
        youWonToast: "ተሰናኢልካ!",
        currentBall: "ሕጂ ዝወጸ ኳስ",
        prizeTable: "ሰንጠረዝ ሽልማት",
        hidePrizes: "ምስዓር",
        showPrizes: "ምርኣይ",
        multiplier: "x",
        picks: "ምርጫ",
        possibleWin: "ሽልማት",
        ticketsRemaining: "ዝተረፉ ቲከታት",
        maxTicketsReached: "ከፍተኛ ቲከታት በጺሕካ",
        yourTickets: "ቲከታትኩም",
        id: "መለለዪ",
        showMore: "ኩሎም ኣርኣይ",
        showLess: "ሓጺሩ ኣርኣይ",
        yourStats: "ስታቲስቲክሳትኩም",
        topWinners: "ላዕለዎት ተሓተንቲ",
        noWinnersYet: "ሕጂ ተሓታንቲ የለዉን",
        rank: "ደረጃ",
        player: "ተጻዋታይ",
        amount: "ብዝሒ",
        recentDraws: "ዳሕረዎት ዕጽብታት",
        noDrawsYet: "ዕጽብታት ኣይተካየዱን",
        drawNumber: "ዕጽብ",
        selectVariant: "አይነት ጸወታ",
        ticketsAllowed: "ከፍተኛ ቲከታት",
        minBet: "ዝቃና",
        maxBet: "ዝሓበረ",
        errorLoading: "ስሕተት ጽዕና ውሂብ. በጃኻ ገጹ ሓድስዎ።",
        retry: "እንደገና ፈትን",
      },
      wo: {
        title: "KEENO",
        gameId: "Gitaa ID",
        timeRemaining: "Wodiya Aysuwaa",
        players: "Asooggidiyaa",
        myTickets: "Tikiitiya Taa",
        selectNumbers: "Kiishsha Doora",
        quick: "Keeniya",
        clear: "Mulqqa",
        addTicket: "Tikiiti Ossa",
        drawInProgress: "Kusuwaa Karatida",
        progress: "Wonna",
        drawnNumbers: "Kiishsha Biyuwa",
        gameCompleted: "Gitaa Amarsiyo",
        youWon: "Wontitta",
        betterLuck: "Bollaara woojjaa maynni!",
        newGameStarting: "Bollaara gitaa baddana...",
        active: "Ossanawka",
        won: "Wonttis",
        lost: "Yels",
        bet: "Xaaphuwa",
        numbers: "kiishsha",
        matches: "Mallanchiyaa",
        history: "Sinttena",
        results: "Bollaara",
        stats: "Haggarraa",
        leaderboard: "Wonttidaa Asaar",
        totalGames: "Gitaa",
        winRate: "Wonna Wontta",
        wins: "Wontsiyaa",
        totalWinAmount: "Wonttidu",
        winners: "Wonttidaa",
        noTickets: "Tikiiti Aybakk'o",
        selectHint: "Kiishsha Dooraa, tikiiti ossa",
        loading: "Buxxuwaagade...",
        pleaseWait: "Huu gitaa baddanaagaa koyye",
        drawInProgressMsg: "Kusuwaa karatida, kiishshi dooran danddayeenna",
        gameCompletedMsg: "Gitaa amarsiyo, bollaara gitaa baddana",
        maxNumbers: "Sirriitti doorana doshshi",
        selectExactly: "Sirriitti doora",
        numbersToPick: "kiishsha",
        cannotAddTicket: "Awudiyani tikiiti ossiya danddayeenna",
        ticketAdded: "Tikiiti ossiys!",
        failedAddTicket: "Tikiiti ossiya danddayennabeem",
        youWonToast: "Wontitta!",
        currentBall: "Haashuwoolan Bola",
        prizeTable: "Shilimatu Xaafaa",
        hidePrizes: "Uuma",
        showPrizes: "Be'eyyiza",
        multiplier: "x",
        picks: "Dooramaa",
        possibleWin: "Danddayicc Shilima",
        ticketsRemaining: "Haarid Tikiitiya",
        maxTicketsReached: "Tikiitiya Ayfu gecce",
        yourTickets: "Hurta Tikiitiya",
        id: "ID",
        showMore: "Kuma Be'eyyiza",
        showLess: "Shiiccishsha Be'eyyiza",
        yourStats: "Hurta Haggarraa",
        topWinners: "Wonttidaa Asaar",
        noWinnersYet: "Wonttidaa Aybakk'o",
        rank: "Darejaa",
        player: "Asooggidaa",
        amount: "Gitaa",
        recentDraws: "Bollaara Kusuwaa",
        noDrawsYet: "Kusuwai Bollaara Aybakk'o",
        drawNumber: "Kusuwaa",
        selectVariant: "Gitaa Doora",
        ticketsAllowed: "Tikiitiya Ayfu",
        minBet: "Shiixshsha",
        maxBet: "Woyyidda",
        errorLoading: "Dogoggora fe'uwwaa. Mee fuula haaromsi.",
        retry: "Itti deebi'i",
      },
    };
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const getMultiplier = (matchCount, variant = selectedVariant) => {
    const multipliers = prizeMultipliers[variant];
    if (!multipliers) return 0;
    return multipliers[matchCount] || 0;
  };

  const getWinAmount = (matchCount, stake, variant = selectedVariant) => {
    return stake * getMultiplier(matchCount, variant);
  };

  // Fetch available variants
  const fetchVariants = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/keno/multipliers");

      if (
        response &&
        response.data &&
        response.data.success &&
        response.data.variants
      ) {
        const variantsData = response.data.variants;

        if (
          variantsData &&
          typeof variantsData === "object" &&
          Object.keys(variantsData).length > 0
        ) {
          setAvailableVariants(variantsData);

          const newMultipliers = {};
          Object.keys(variantsData).forEach((key) => {
            const variant = variantsData[key];
            if (variant && variant.multipliers) {
              newMultipliers[key] = variant.multipliers;
            }
          });
          setPrizeMultipliers(newMultipliers);

          const defaultVariant = response.data.defaultVariant || 10;
          if (variantsData[defaultVariant]) {
            const variant = variantsData[defaultVariant];
            setSelectedVariant(defaultVariant);
            setNumbersRequired(variant.numbersRequired || defaultVariant);
            setMaxTickets(variant.maxTickets || 2);
            setVariantAllowedStakes(
              variant.allowedStakes || [10, 20, 50, 100, 200, 500, 1000],
            );
            setVariantMinStake(variant.minStake || 10);
            setVariantMaxStake(variant.maxStake || 1000);
            
            // Set initial stake amount
            const initialStake = variant.allowedStakes?.[0] || variant.minStake || 10;
            setStakeAmount(initialStake);
          }
          setVariantsLoaded(true);
          setApiError(false);
          return;
        }
      }
      setApiError(true);
      setVariantsLoaded(false);
    } catch (error) {
      setApiError(true);
      setVariantsLoaded(false);
    }
  }, []);

  // Fetch user statistics
  const fetchUserStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/keno/user-stats");
      if (response.data.success) {
        setStatistics({
          totalGames: Number(response.data.stats.totalGames) || 0,
          totalWins: Number(response.data.stats.totalWins) || 0,
          totalWinAmount: Number(response.data.stats.totalWinAmount) || 0,
        });
      }
    } catch (error) {
      // Silent fail
    }
  }, []);

  // Fetch top winners
  const fetchTopWinners = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/keno/top-winners");
      if (response.data.success) {
        const winners = response.data.winners || [];
        const sanitizedWinners = winners.map((winner) => ({
          ...winner,
          amount: Number(winner?.amount) || Number(winner?.total_wins) || 0,
        }));
        setTopWinners(Array.isArray(sanitizedWinners) ? sanitizedWinners : []);
      } else {
        setTopWinners([]);
      }
    } catch (error) {
      setTopWinners([]);
    }
  }, []);

  // Fetch recent draws
  const fetchRecentDrawsDirect = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/keno/recent-draws");
      if (response.data.success) {
        setRecentDraws(response.data.draws || []);
      } else {
        const historyResponse = await axiosInstance.get("/keno/history");
        if (historyResponse.data.success) {
          const completedGames = historyResponse.data.games
            .filter(
              (game) =>
                game.status === "completed" &&
                game.drawn_numbers &&
                game.drawn_numbers.length > 0,
            )
            .slice(0, 10);
          setRecentDraws(completedGames);
        }
      }
    } catch (error) {
      setRecentDraws([]);
    }
  }, []);

  // Server-synced timer
  const startServerSyncedTimer = useCallback(
    (serverRemaining) => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      let currentRemaining = serverRemaining;
      setTimeRemaining(currentRemaining);

      timerIntervalRef.current = setInterval(() => {
        if (currentRemaining > 0 && sessionStatus === "waiting") {
          currentRemaining--;
          setTimeRemaining(currentRemaining);
        } else if (currentRemaining <= 0) {
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
        }
      }, 1000);
    },
    [sessionStatus],
  );

  // Fetch active session
  const fetchActiveSession = useCallback(async () => {
    if (!variantsLoaded) return;
    
    try {
      const response = await axiosInstance.get("/keno/active-session");
      if (response.data.success) {
        const newStatus = response.data.status;
        const newGameId = response.data.gameId;

        if (lastGameIdRef.current && lastGameIdRef.current !== newGameId) {
          resetForNewGame();
        }
        lastGameIdRef.current = newGameId;

        setTotalPlayers(response.data.totalPlayers || 0);
        setCurrentGameId(newGameId);
        setSessionStatus(newStatus);

        if (
          response.data.variant &&
          userSelectedVariant === null &&
          newStatus !== "waiting"
        ) {
          setSelectedVariant(response.data.variant);
          setNumbersRequired(
            response.data.numbersRequired || response.data.variant,
          );
          setMaxTickets(response.data.maxTicketsPerUser || 2);
          if (response.data.allowedStakes) {
            setVariantAllowedStakes(response.data.allowedStakes);
          }
          if (response.data.minStake) {
            setVariantMinStake(response.data.minStake);
            if (stakeAmount < response.data.minStake) {
              setStakeAmount(response.data.minStake);
            }
          }
          if (response.data.maxStake) {
            setVariantMaxStake(response.data.maxStake);
          }
        }

        if (newStatus === "waiting") {
          const serverRemaining = response.data.timeRemaining || 0;
          startServerSyncedTimer(serverRemaining);
        } else {
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          setTimeRemaining(0);
        }

        if (newStatus === "drawing") {
          setIsDrawing(true);
          setGameCompleted(false);

          if (
            response.data.drawnNumbers &&
            Array.isArray(response.data.drawnNumbers)
          ) {
            const newDrawnNumbers = response.data.drawnNumbers;

            if (
              newDrawnNumbers.length > drawnNumbers.length &&
              newDrawnNumbers.length > 0
            ) {
              const newBall = newDrawnNumbers[newDrawnNumbers.length - 1];
              if (newBall !== lastBallRef.current) {
                lastBallRef.current = newBall;
                setCurrentDrawBall(newBall);
                setRotateCurrentBall(true);
                setTimeout(() => setRotateCurrentBall(false), 600);
              }
            }

            setDrawnNumbers(newDrawnNumbers);
            setDrawProgress((newDrawnNumbers.length / 20) * 100);

            setMyTickets((prev) =>
              prev.map((ticket) => ({
                ...ticket,
                matchedNumbers: ticket.numbers.filter((n) =>
                  newDrawnNumbers.includes(n),
                ),
                matchCount: ticket.numbers.filter((n) =>
                  newDrawnNumbers.includes(n),
                ).length,
              })),
            );
          }

          if (
            response.data.currentBall &&
            response.data.currentBall !== lastBallRef.current
          ) {
            lastBallRef.current = response.data.currentBall;
            setCurrentDrawBall(response.data.currentBall);
          }
        } else if (newStatus === "completed") {
          setIsDrawing(false);
          if (!hasFetchedResults && !gameCompleted) {
            setHasFetchedResults(true);
            await fetchResults();
          }
        } else if (newStatus === "waiting") {
          setIsDrawing(false);
          if (gameCompleted) resetForNewGame();
        }

        if (response.data.tickets && Array.isArray(response.data.tickets)) {
          const formattedTickets = response.data.tickets.map((ticket) => ({
            ticketIndex: ticket.ticketIndex,
            numbers: ticket.selectedNumbers,
            stake: ticket.stakeAmount,
            numbersCount: ticket.numbersCount || numbersRequired,
            gameId: ticket.gameId,
            status: "ACTIVE",
            matchedNumbers: drawnNumbers.filter((n) =>
              ticket.selectedNumbers?.includes(n),
            ),
            matchCount:
              ticket.selectedNumbers?.filter((n) => drawnNumbers.includes(n))
                .length || 0,
            winAmount: 0,
          }));
          setMyTickets(formattedTickets);
        }
      }
    } catch (error) {
      // Silent fail
    }
  }, [
    drawnNumbers,
    gameCompleted,
    hasFetchedResults,
    numbersRequired,
    stakeAmount,
    startServerSyncedTimer,
    userSelectedVariant,
    variantsLoaded,
  ]);

  const fetchResults = async () => {
    try {
      const response = await axiosInstance.get("/keno/results");
      if (response.data.success && response.data.hasResults) {
        const totalWin = response.data.summary?.totalWin || 0;

        if (totalWin > 0) {
          setGameEndMessage({ type: "win", amount: totalWin });
          setLastWinAmount(totalWin);
          setShowWinToast(true);
          if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
          toastTimeoutRef.current = setTimeout(
            () => setShowWinToast(false),
            5000,
          );
        } else {
          setGameEndMessage({ type: "lose" });
        }
        setGameCompleted(true);

        fetchGameHistory();
        fetchUserStats();

        if (response.data.results && Array.isArray(response.data.results)) {
          setMyTickets((prev) => {
            const updatedTickets = prev.map((ticket) => {
              const result = response.data.results.find(
                (r) =>
                  String(r.gameId) === String(ticket.gameId) ||
                  String(r.ticketIndex) === String(ticket.ticketIndex),
              );
              if (result) {
                return {
                  ...ticket,
                  winAmount: result.winAmount,
                  matchCount: result.matchCount,
                  multiplier: result.multiplier,
                  status: result.winAmount > 0 ? "WON" : "LOST",
                };
              }
              return { ...ticket, status: "LOST", winAmount: 0 };
            });
            return updatedTickets;
          });
        }

        setTimeout(() => {
          if (!isDrawing && gameCompleted) {
            resetForNewGame();
            fetchActiveSession();
          }
        }, 5000);
      } else {
        setTimeout(() => {
          setHasFetchedResults(false);
          fetchActiveSession();
        }, 2000);
      }
    } catch (error) {
      setTimeout(() => {
        setHasFetchedResults(false);
        fetchActiveSession();
      }, 2000);
    }
  };

  const fetchGameHistory = async () => {
    try {
      const response = await axiosInstance.get("/keno/history");
      if (response.data.success) {
        setGameHistory(response.data.games);
        const wins = response.data.games.filter((g) => g.win_amount > 0).length;
        const totalWin = response.data.games.reduce(
          (sum, g) => sum + (g.win_amount || 0),
          0,
        );
        setStatistics({
          totalGames: response.data.games.length,
          totalWins: wins,
          totalWinAmount: totalWin,
        });
      }
    } catch (error) {
      // Silent fail
    }
  };

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/user/profile");
      if (response.data.success) {
        localStorage.setItem("userId", response.data.user.id);
      }
    } catch (error) {
      if (error.response?.status === 401) navigate("/login");
    }
  }, [navigate]);

  const resetForNewGame = useCallback(() => {
    setSelectedNumbers([]);
    setDrawnNumbers([]);
    setCurrentDrawBall(null);
    setIsDrawing(false);
    setDrawProgress(0);
    setGameCompleted(false);
    setRotateCurrentBall(false);
    setGameEndMessage(null);
    setHasFetchedResults(false);
    setSessionStatus("waiting");
    setMyTickets([]);
    lastBallRef.current = null;
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  }, []);

  // Handle variant change from dropdown
  const handleVariantChange = (e) => {
    const variantId = parseInt(e.target.value);
    const variant = availableVariants[variantId];

    if (variant) {
      setUserSelectedVariant(variantId);
      setSelectedVariant(variantId);
      setNumbersRequired(variant.numbersRequired);
      setSelectedNumbers([]);
      setMaxTickets(variant.maxTickets);
      setVariantAllowedStakes(variant.allowedStakes);
      setVariantMinStake(variant.minStake);
      setVariantMaxStake(variant.maxStake);

      let newStake = stakeAmount;
      if (stakeAmount < variant.minStake) {
        newStake = variant.minStake;
      }
      if (stakeAmount > variant.maxStake) {
        newStake = variant.maxStake;
      }
      setStakeAmount(newStake);
    }
  };

  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      await fetchUserProfile();
      await fetchVariants();
      await fetchGameHistory();
      await fetchUserStats();
      await fetchTopWinners();
      await fetchRecentDrawsDirect();
      setIsInitialized(true);
    };
    initializeData();
  }, []);

  // Load active session after variants are loaded
  useEffect(() => {
    if (variantsLoaded && isInitialized) {
      fetchActiveSession();
    }
  }, [variantsLoaded, isInitialized, fetchActiveSession]);

  // Poll for updates
  useEffect(() => {
    if (!isInitialized || !variantsLoaded) return;

    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    const intervalTime = isDrawing ? 1000 : 3000;

    pollIntervalRef.current = setInterval(() => {
      fetchActiveSession();
      if (activeTab === "stats") {
        fetchUserStats();
      }
      if (activeTab === "leaderboard") {
        fetchTopWinners();
      }
      if (activeTab === "results") {
        fetchRecentDrawsDirect();
      }
    }, intervalTime);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [
    fetchActiveSession,
    isInitialized,
    variantsLoaded,
    isDrawing,
    activeTab,
    fetchUserStats,
    fetchTopWinners,
    fetchRecentDrawsDirect,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const canSelectNumbers =
    sessionStatus === "waiting" &&
    !isDrawing &&
    !gameCompleted &&
    myTickets.length < maxTickets;

  const toggleNumber = (num) => {
    if (!canSelectNumbers) {
      if (myTickets.length >= maxTickets) {
        toast.info(t("maxTicketsReached"));
      } else if (sessionStatus !== "waiting") {
        toast.info(t("pleaseWait"));
      } else if (isDrawing) {
        toast.info(t("drawInProgressMsg"));
      } else if (gameCompleted) {
        toast.info(t("gameCompletedMsg"));
      }
      return;
    }

    setSelectedNumbers((prev) => {
      if (prev.includes(num)) {
        return prev.filter((n) => n !== num);
      }
      if (prev.length >= numbersRequired) {
        toast.warning(
          `${t("maxNumbers")} ${numbersRequired} ${t("numbersToPick")}`,
        );
        return prev;
      }
      return [...prev, num].sort((a, b) => a - b);
    });
  };

  const quickPick = () => {
    if (!canSelectNumbers) {
      toast.info(t("pleaseWait"));
      return;
    }
    const nums = new Set();
    while (nums.size < numbersRequired) {
      nums.add(Math.floor(Math.random() * 80) + 1);
    }
    setSelectedNumbers(Array.from(nums).sort((a, b) => a - b));
  };

  const clearSelections = () => {
    if (!canSelectNumbers) {
      toast.info(t("pleaseWait"));
      return;
    }
    setSelectedNumbers([]);
  };

  const addTicket = async () => {
    if (!canSelectNumbers) {
      if (myTickets.length >= maxTickets) {
        toast.error(t("maxTicketsReached"));
      } else {
        toast.error(t("cannotAddTicket"));
      }
      return;
    }
    if (selectedNumbers.length !== numbersRequired) {
      toast.error(
        `${t("selectExactly")} ${numbersRequired} ${t("numbersToPick")}`,
      );
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post("/keno/join", {
        stakeAmount,
        selectedNumbers,
        variant: selectedVariant,
      });

      if (response.data.success) {
        const newTicket = {
          ticketIndex: response.data.ticket?.ticketIndex,
          gameId: response.data.ticket?.gameId,
          numbers: [...selectedNumbers],
          stake: stakeAmount,
          numbersCount: numbersRequired,
          status: "ACTIVE",
          matchedNumbers: [],
          matchCount: 0,
          winAmount: 0,
        };
        setMyTickets((prev) => [newTicket, ...prev]);
        setSelectedNumbers([]);
        toast.success(
          `${t("ticketAdded")} #${response.data.ticket?.ticketIndex}`,
        );
        await fetchActiveSession();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t("failedAddTicket"));
    } finally {
      setLoading(false);
    }
  };

  const getDisplayGameId = () => {
    if (!currentGameId) return "------";
    return String(currentGameId).slice(-9);
  };

  // Render prize table
  const renderPrizeTable = () => {
    const currentMultipliers = prizeMultipliers[selectedVariant];
    if (!currentMultipliers) return null;

    const prizeRows = [];
    for (let matches = numbersRequired; matches >= 0; matches--) {
      const multiplier = currentMultipliers[matches];
      if (multiplier !== undefined && multiplier > 0) {
        prizeRows.push(
          <div
            key={matches}
            className="flex justify-between items-center py-1 border-b border-gray-700/30 last:border-0"
          >
            <span className="text-gray-300 text-xs w-10">{matches}</span>
            <span className="text-orange-400 font-bold text-sm w-14 text-center">
              {multiplier}x
            </span>
            <span className="text-green-400 font-semibold text-xs w-20 text-right">
              {(stakeAmount * multiplier).toFixed(0)} ETB
            </span>
          </div>,
        );
      }
    }

    return (
      <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl p-3 mb-3 border border-gray-800/50">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <span className="text-base">🏆</span>
            <p className="text-white font-semibold text-xs">
              {t("prizeTable")}
            </p>
            <span className="text-gray-500 text-[10px]">
              ({numbersRequired} {t("picks")})
            </span>
          </div>
          <button
            onClick={() => setShowPrizeTable(!showPrizeTable)}
            className="text-gray-400 text-[10px] hover:text-orange-400 transition-colors px-2 py-0.5 rounded-lg bg-gray-800/50"
          >
            {showPrizeTable ? "▲ Hide" : "▼ Show"}
          </button>
        </div>
        {showPrizeTable && (
          <div className="bg-gray-800/30 rounded-lg p-2">
            <div className="flex justify-between text-gray-500 text-[10px] font-semibold pb-1 border-b border-gray-700 mb-1">
              <span className="w-10">Matches</span>
              <span className="w-14 text-center">Multiplier</span>
              <span className="w-20 text-right">Prize</span>
            </div>
            {prizeRows}
          </div>
        )}
      </div>
    );
  };

  // Render variant selector
  const renderVariantSelector = () => {
    if (isDrawing || gameCompleted) return null;

    if (apiError) {
      return (
        <div className="bg-red-900/30 backdrop-blur-sm rounded-xl p-4 mb-3 border border-red-800/50">
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            <span className="text-red-400 text-sm">⚠️</span>
            <p className="text-red-300 text-xs">{t("errorLoading")}</p>
            <button
              onClick={() => {
                setApiError(false);
                setVariantsLoaded(false);
                fetchVariants();
              }}
              className="mt-2 px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg transition-colors"
            >
              {t("retry")}
            </button>
          </div>
        </div>
      );
    }

    if (
      !variantsLoaded ||
      !availableVariants ||
      Object.keys(availableVariants).length === 0
    ) {
      return (
        <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl p-3 mb-3 border border-gray-800/50">
          <div className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-400 text-xs">{t("loading")}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl p-3 mb-3 border border-gray-800/50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-base">🎮</span>
            <label className="text-gray-300 text-xs font-medium">
              {t("selectVariant")}
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedVariant}
              onChange={handleVariantChange}
              disabled={!canSelectNumbers}
              className={`bg-gray-800 text-white px-3 py-1.5 rounded-lg text-xs border border-gray-700 focus:border-orange-500 focus:outline-none transition-all ${
                !canSelectNumbers
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:border-gray-600"
              }`}
            >
              {Object.entries(availableVariants).map(([id, variant]) => (
                <option key={id} value={id}>
                  {variant?.emoji || "🎲"}{" "}
                  {variant?.name || variant?.nameEn || `Variant ${id}`}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-2 text-[10px] text-gray-500">
              <span className="flex items-center gap-0.5">
                <span>🎟️</span>
                <span>{maxTickets}</span>
              </span>
              <span className="w-px h-2.5 bg-gray-700"></span>
              <span className="flex items-center gap-0.5">
                <span>💰</span>
                <span>
                  {variantMinStake}-{variantMaxStake}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNumbers = () => {
    if (isDrawing || gameCompleted) return null;

    const rows = [];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
    const numbersPerRow = isMobile ? 8 : 10;
    const totalRows = Math.ceil(TOTAL_NUMBERS / numbersPerRow);

    for (let row = 0; row < totalRows; row++) {
      const cells = [];
      for (let col = 0; col < numbersPerRow; col++) {
        const num = row * numbersPerRow + col + 1;
        if (num > TOTAL_NUMBERS) break;
        const isSelected = selectedNumbers.includes(num);
        cells.push(
          <KenoBall
            key={num}
            number={num}
            size="xxs"
            isSelected={isSelected}
            onClick={toggleNumber}
          />,
        );
      }
      rows.push(
        <div key={row} className="flex justify-center gap-1 mb-1 flex-wrap">
          {cells}
        </div>,
      );
    }
    return rows;
  };

  const renderDrawAnimation = () => {
    if (!isDrawing) return null;
    const displayBall =
      currentDrawBall ||
      (drawnNumbers.length > 0 ? drawnNumbers[drawnNumbers.length - 1] : null);
    const isMobile = typeof window !== "undefined" && window.innerWidth < 640;

    const getDrawnNumbersRows = () => {
      const actualDrawnNumbers = drawnNumbers.filter(
        (num) => num !== currentDrawBall,
      );
      if (actualDrawnNumbers.length === 0) return null;

      const ballsPerRow = isMobile ? 7 : 10;
      const rows = [];

      for (let i = 0; i < actualDrawnNumbers.length; i += ballsPerRow) {
        const rowBalls = actualDrawnNumbers.slice(i, i + ballsPerRow);
        rows.push(
          <div
            key={i}
            className={`flex gap-1 mb-1 ${isMobile ? "justify-center" : "justify-start"} flex-wrap`}
          >
            {rowBalls.map((num, idx) => (
              <KenoBall
                key={`${i}-${idx}`}
                number={num}
                size="xs"
                isDrawn={true}
              />
            ))}
          </div>,
        );
      }
      return <div>{rows}</div>;
    };

    return (
      <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl p-4 mb-3 border border-gray-800/50">
        <div className="text-center mb-3">
          <div className="text-orange-400 text-xs font-semibold mb-1">
            {t("drawInProgress")}
          </div>
          <div className="flex justify-center items-center min-h-[90px]">
            {displayBall ? (
              <div className={`${rotateCurrentBall ? "animate-tumble" : ""}`}>
                <KenoBall
                  number={displayBall}
                  size="lg"
                  isCurrent={true}
                  shouldRotate={true}
                />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center animate-pulse border border-gray-700">
                <span className="text-gray-500 text-xl">?</span>
              </div>
            )}
          </div>
          <p className="text-gray-500 text-[10px] mt-1">{t("currentBall")}</p>
        </div>

        <div className="mb-2">
          <div className="flex justify-between text-gray-400 text-[10px] mb-0.5">
            <span>{t("progress")}</span>
            <span className="text-orange-400 font-mono text-xs">
              {drawnNumbers.length}/20
            </span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-1.5">
            <div
              className="bg-gradient-to-r from-orange-500 to-yellow-500 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${drawProgress}%` }}
            />
          </div>
        </div>

        <div>
          <p className="text-gray-400 text-[10px] mb-1">{t("drawnNumbers")}</p>
          <div className="max-h-32 overflow-y-auto">
            {getDrawnNumbersRows()}
          </div>
        </div>
      </div>
    );
  };

  const renderGameCompletion = () => {
    if (!gameCompleted) return null;
    const isWin = gameEndMessage?.type === "win";
    const winAmount = gameEndMessage?.amount || 0;
    return (
      <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-sm rounded-xl p-4 mb-3 text-center border border-gray-700/50">
        <div className="text-3xl mb-1">{isWin ? "🎉🏆🎉" : "😢"}</div>
        <div
          className={`font-bold text-sm ${isWin ? "text-green-400" : "text-gray-400"}`}
        >
          {isWin
            ? `${t("youWon")} ${winAmount.toLocaleString()} ETB!`
            : t("gameCompleted")}
        </div>
        {!isWin && (
          <div className="text-gray-500 text-xs mt-0.5">{t("betterLuck")}</div>
        )}
        <div className="text-gray-600 text-[10px] mt-1 animate-pulse">
          {t("newGameStarting")}
        </div>
      </div>
    );
  };

  const renderTicketsGrid = () => {
    if (myTickets.length === 0 && !isDrawing && !gameCompleted) {
      return (
        <div className="text-center py-6 text-gray-500 text-xs bg-gray-800/30 rounded-xl">
          <div className="text-2xl mb-1">🎫</div>
          {t("noTickets")}
          <br />
          <span className="text-[10px]">{t("selectHint")}</span>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {myTickets.map((ticket, idx) => {
          const isWinner = ticket.status === "WON" || ticket.winAmount > 0;
          const isLost = ticket.status === "LOST";
          const winAmountValue =
            ticket.winAmount > 0
              ? ticket.winAmount
              : getWinAmount(
                  ticket.matchCount,
                  ticket.stake,
                  ticket.numbersCount || selectedVariant,
                );
          const multiplierValue =
            ticket.multiplier ||
            getMultiplier(
              ticket.matchCount,
              ticket.numbersCount || selectedVariant,
            );

          return (
            <div
              key={idx}
              className={`bg-gray-800/50 rounded-xl p-2 transition-all ${
                isWinner
                  ? "ring-1 ring-green-500/50"
                  : isLost
                    ? "ring-1 ring-red-500/30"
                    : "ring-1 ring-yellow-500/30"
              }`}
            >
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-1">
                  <span className="text-orange-400 font-bold text-xs">
                    #{ticket.ticketIndex || idx + 1}
                  </span>
                  <span className="text-gray-600 text-[9px]">
                    ({ticket.numbersCount || numbersRequired})
                  </span>
                </div>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    isWinner
                      ? "bg-green-500/20 text-green-400"
                      : isLost
                        ? "bg-red-500/20 text-red-400"
                        : "bg-yellow-500/20 text-yellow-400"
                  }`}
                >
                  {isWinner
                    ? `+${winAmountValue.toFixed(0)}`
                    : isLost
                      ? t("lost")
                      : t("active")}
                </span>
              </div>

              <div
                className={`grid grid-cols-${Math.min(5, ticket.numbersCount || numbersRequired)} gap-0.5 mb-1`}
              >
                {ticket.numbers.map((num, i) => {
                  const isMatched =
                    ticket.matchedNumbers?.includes(num) ||
                    (ticket.matchCount > 0 &&
                      ticket.numbers.includes(num) &&
                      drawnNumbers.includes(num));
                  return (
                    <div
                      key={i}
                      className={`flex items-center justify-center py-0.5 rounded-md text-[11px] font-bold ${
                        isMatched && (isDrawing || isWinner)
                          ? "bg-green-600 text-white"
                          : isWinner && !isDrawing
                            ? "bg-green-500/30 text-green-400"
                            : "bg-gray-700/70 text-gray-300"
                      }`}
                    >
                      {num}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between text-[9px] pt-1 border-t border-gray-700/50">
                <span className="text-gray-500">
                  {ticket.numbersCount || numbersRequired} {t("numbers")}
                </span>
                <span className="text-gray-400 font-mono">
                  {ticket.stake} ETB
                </span>
              </div>

              {(ticket.matchCount > 0 || isWinner) && (
                <div className="text-[9px] text-center mt-1 pt-0.5">
                  {isWinner ? (
                    <span className="text-green-400 font-semibold">
                      {ticket.matchCount}/
                      {ticket.numbersCount || numbersRequired} ×{" "}
                      {multiplierValue} = +{winAmountValue.toFixed(0)}
                    </span>
                  ) : isDrawing && ticket.matchCount > 0 ? (
                    <span className="text-green-400 animate-pulse">
                      {ticket.matchCount} →{" "}
                      {getWinAmount(
                        ticket.matchCount,
                        ticket.stake,
                        ticket.numbersCount || selectedVariant,
                      ).toFixed(0)}
                    </span>
                  ) : ticket.matchCount > 0 ? (
                    <span className="text-yellow-400">
                      {ticket.matchCount}/
                      {ticket.numbersCount || numbersRequired}
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Loading state
  if (!isInitialized || (!variantsLoaded && !apiError)) {
    return (
      <PageWrapper {...props}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-gray-400 text-xs">{t("loading")}</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper {...props}>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        theme="dark"
        style={{ zIndex: 9999 }}
      />

      <div className="relative z-0 pt-2 pb-4 px-3 sm:px-4 pointer-events-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-white via-orange-300 to-orange-500 bg-clip-text text-transparent">
              {t("title")}
            </h1>
            <div className="bg-gray-900/60 backdrop-blur-sm rounded-lg px-2 py-1 border border-gray-800">
              <p className="text-gray-400 text-[9px]">{t("id")}</p>
              <p className="text-white font-mono text-xs font-semibold">
                {getDisplayGameId()}
              </p>
            </div>
          </div>

          {/* Variant Selector */}
          {renderVariantSelector()}

          {/* Prize Table */}
          {!isDrawing && !gameCompleted && renderPrizeTable()}

          {/* Timer Display */}
          {sessionStatus === "waiting" && !isDrawing && !gameCompleted && (
            <div className="flex justify-center mb-4">
              <BraunClock
                countdown={timeRemaining}
                gameStatus={sessionStatus}
              />
            </div>
          )}

          {/* Draw Animation */}
          {renderDrawAnimation()}

          {/* Game Completion */}
          {renderGameCompletion()}

          {/* Number Selection */}
          {!isDrawing && !gameCompleted && (
            <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl p-3 mb-3 border border-gray-800/50">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                <div className="flex gap-1.5 flex-wrap">
                  <select
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(Number(e.target.value))}
                    disabled={!canSelectNumbers}
                    className={`bg-gray-800 text-white px-2 py-1 rounded-lg text-xs border border-gray-700 focus:border-orange-500 focus:outline-none transition-all ${
                      !canSelectNumbers
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:border-gray-600"
                    }`}
                  >
                    {variantAllowedStakes.map((amt) => (
                      <option key={amt} value={amt}>
                        {amt} ETB
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={quickPick}
                    disabled={!canSelectNumbers}
                    className={`bg-blue-600/80 text-white px-2 py-1 rounded-lg text-xs font-semibold transition-all ${
                      !canSelectNumbers
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-blue-600"
                    }`}
                  >
                    🎲 {t("quick")} {numbersRequired}
                  </button>

                  <button
                    onClick={clearSelections}
                    disabled={!canSelectNumbers}
                    className={`bg-gray-700 text-white px-2 py-1 rounded-lg text-xs font-semibold transition-all ${
                      !canSelectNumbers
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-gray-600"
                    }`}
                  >
                    🗑 {t("clear")}
                  </button>
                </div>
                <div className="bg-orange-500/20 rounded-lg px-3 py-1">
                  <p className="text-orange-400 font-bold text-sm">
                    {selectedNumbers.length}/{numbersRequired}
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">{renderNumbers()}</div>

              <button
                onClick={addTicket}
                disabled={
                  loading ||
                  selectedNumbers.length !== numbersRequired ||
                  !canSelectNumbers
                }
                className={`w-full mt-3 py-2 rounded-xl font-bold text-sm transition-all ${
                  loading ||
                  selectedNumbers.length !== numbersRequired ||
                  !canSelectNumbers
                    ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white shadow-lg hover:shadow-xl"
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t("loading")}
                  </div>
                ) : (
                  `➕ ${t("addTicket")} - ${stakeAmount} ETB`
                )}
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl border border-gray-800/50 overflow-hidden">
            <div className="flex overflow-x-auto border-b border-gray-800">
              {["game", "history", "results", "stats", "leaderboard"].map(
                (tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-2 font-semibold text-[11px] whitespace-nowrap transition-all ${
                      activeTab === tab
                        ? "text-orange-400 border-b-2 border-orange-400 bg-orange-500/5"
                        : "text-gray-500 hover:text-gray-300"
                    }`}
                  >
                    {t(tab)}
                  </button>
                ),
              )}
            </div>

            <div className="p-3">
              {activeTab === "game" && (
                <div>
                  <p className="text-gray-400 text-[10px] mb-2">
                    📋 {t("yourTickets")} ({myTickets.length}/{maxTickets})
                  </p>
                  {renderTicketsGrid()}
                </div>
              )}

              {activeTab === "history" && (
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {gameHistory.length === 0 ? (
                    <div className="text-center text-gray-500 text-xs py-6">
                      {t("noTickets")}
                    </div>
                  ) : (
                    gameHistory.slice(0, 10).map((game) => (
                      <div
                        key={game.id}
                        className="bg-gray-800/50 rounded-lg p-2 flex justify-between items-center text-xs hover:bg-gray-800 transition-all"
                      >
                        <span className="text-orange-400 font-mono font-bold">
                          #{game.id}
                        </span>
                        <span className="text-gray-300">
                          {game.numbers_count || 10} {t("numbers")}
                        </span>
                        <span className="text-gray-300 font-mono">
                          {game.stake_amount} ETB
                        </span>
                        <span
                          className={`font-bold ${game.win_amount > 0 ? "text-green-400" : "text-red-400"}`}
                        >
                          {game.win_amount > 0
                            ? `+${game.win_amount}`
                            : game.win_amount}{" "}
                          ETB
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "results" && (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  <p className="text-gray-400 text-[10px] mb-1">
                    🎲 {t("recentDraws")}
                  </p>
                  {Array.isArray(recentDraws) && recentDraws.length > 0 ? (
                    recentDraws.map((draw, idx) => {
                      const isExpanded = expandedDraws[draw.id || idx];
                      const drawnNumbersArray = draw.drawn_numbers || [];
                      const displayNumbers = isExpanded
                        ? drawnNumbersArray
                        : drawnNumbersArray.slice(0, 10);
                      const hasMore = drawnNumbersArray.length > 10;

                      return (
                        <div
                          key={draw.id || idx}
                          className="bg-gray-800/50 rounded-lg p-2 hover:bg-gray-800/70 transition-all"
                        >
                          <div className="flex justify-between items-center mb-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-orange-400 font-mono font-bold text-xs">
                                #{draw.id || idx + 1}
                              </span>
                              <span className="text-gray-600 text-[9px]">
                                {draw.numbers_count || 10} nums
                              </span>
                            </div>
                            <button
                              onClick={() => toggleDrawExpand(draw.id || idx)}
                              className="text-gray-500 text-[10px] hover:text-orange-400 transition-colors"
                            >
                              {isExpanded ? "▲ Less" : "▼ All"}
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-1 justify-center">
                            {displayNumbers.map((num, i) => (
                              <span
                                key={i}
                                className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-600/20 text-orange-400 flex items-center justify-center text-[10px] font-bold border border-orange-500/30"
                              >
                                {num}
                              </span>
                            ))}
                          </div>
                          {hasMore && !isExpanded && (
                            <div className="text-center mt-1">
                              <span className="text-gray-600 text-[9px]">
                                +{drawnNumbersArray.length - 10} more
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center text-gray-500 text-xs py-6">
                      {t("noDrawsYet")}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "stats" && (
                <div>
                  <p className="text-gray-400 text-[10px] mb-2">
                    📊 {t("yourStats")}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                      <p className="text-gray-500 text-[9px]">
                        {t("totalGames")}
                      </p>
                      <p className="text-white text-lg font-bold">
                        {statistics?.totalGames || 0}
                      </p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                      <p className="text-gray-500 text-[9px]">{t("winRate")}</p>
                      <p className="text-green-400 text-lg font-bold">
                        {statistics?.totalGames > 0
                          ? (
                              (statistics?.totalWins / statistics?.totalGames) *
                              100
                            ).toFixed(1)
                          : 0}
                        %
                      </p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                      <p className="text-gray-500 text-[9px]">{t("wins")}</p>
                      <p className="text-green-400 text-lg font-bold">
                        {statistics?.totalWins || 0}
                      </p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                      <p className="text-gray-500 text-[9px]">
                        {t("totalWinAmount")}
                      </p>
                      <p className="text-orange-400 text-lg font-bold">
                        {(statistics?.totalWinAmount || 0).toFixed(0)} ETB
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "leaderboard" && (
                <div>
                  <p className="text-gray-400 text-[10px] mb-2">
                    🏆 {t("topWinners")}
                  </p>
                  {Array.isArray(topWinners) && topWinners.length > 0 ? (
                    <div className="space-y-1.5 max-h-80 overflow-y-auto">
                      {topWinners.map((winner, idx) => {
                        let amount = 0;
                        if (winner) {
                          if (typeof winner.amount === "number")
                            amount = winner.amount;
                          else if (typeof winner.amount === "string")
                            amount = parseFloat(winner.amount) || 0;
                          else if (typeof winner.total_wins === "number")
                            amount = winner.total_wins;
                          else if (typeof winner.total_wins === "string")
                            amount = parseFloat(winner.total_wins) || 0;
                        }

                        return (
                          <div
                            key={idx}
                            className="bg-gray-800/50 rounded-lg p-2 flex justify-between items-center text-xs hover:bg-gray-800 transition-all"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`font-bold w-7 ${
                                  idx === 0
                                    ? "text-yellow-400 text-base"
                                    : idx === 1
                                      ? "text-gray-400"
                                      : idx === 2
                                        ? "text-orange-400"
                                        : "text-gray-600"
                                }`}
                              >
                                #{idx + 1}
                              </span>
                              <span className="text-gray-300 font-medium text-xs truncate max-w-[120px]">
                                {winner?.username ||
                                  winner?.full_name ||
                                  "Player"}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-green-400 font-bold text-xs">
                                {amount.toFixed(0)} ETB
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 text-xs py-6">
                      {t("noWinnersYet")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Win Toast */}
          {showWinToast && (
            <div className="fixed top-20 right-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2 rounded-lg shadow-2xl animate-slide-down z-50">
              <div className="flex items-center gap-2">
                <span className="text-lg">🎉</span>
                <div>
                  <p className="font-bold text-[10px]">{t("youWonToast")}</p>
                  <p className="text-xs font-bold">
                    +{lastWinAmount.toLocaleString()} ETB
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
        
        @keyframes slide-down { 
          from { transform: translateY(-20px); opacity: 0; } 
          to { transform: translateY(0); opacity: 1; } 
        }
        .animate-slide-down { animation: slide-down 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); }

        @keyframes lotteryTumble {
          0% { transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg) scale(0.8); opacity: 0.5; }
          50% { transform: rotateX(180deg) rotateY(360deg) rotateZ(90deg) scale(1.2); opacity: 1; }
          100% { transform: rotateX(360deg) rotateY(720deg) rotateZ(360deg) scale(1); opacity: 1; }
        }

        .animate-tumble { animation: lotteryTumble 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; transform-style: preserve-3d; will-change: transform; }
      `}</style>
    </PageWrapper>
  );
};

export default FastKenoGamePage;